import React, { useState, useRef, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table';
import {
  CaretUpFill,
  CaretDownFill,
  Pencil,
  Trash,
  Check,
  X
} from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../../contexts/ProjectContext';
import { useChat } from '../../contexts/ChatContext';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import './ConversationTable.css';

interface Conversation {
  id: string;
  title?: string;
  lastMessage?: string;
  createdAt: Date;
  updatedAt: Date;
  messageCount?: number;
  artifactCount?: number;
  projectId?: string;
}

interface ConversationTableProps {
  conversations: Conversation[];
  onProjectChange: (conversationId: string, projectId: string | null) => void;
  onDelete?: (conversationId: string) => void;
  onUpdate?: (conversationId: string, title: string) => void;
}

const ConversationTable: React.FC<ConversationTableProps> = ({
  conversations,
  onProjectChange,
  onDelete,
  onUpdate
}) => {
  const navigate = useNavigate();
  const { projects } = useProjects();
  const { updateConversation, deleteConversation } = useChat();
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true }
  ]);
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
  const [editedTitle, setEditedTitle] = useState<string>('');
  const editInputRef = useRef<HTMLInputElement>(null);
  const cursorPositionRef = useRef<number>(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<Conversation | null>(null);

  // Restore cursor position after render
  useEffect(() => {
    if (editInputRef.current && editingConversationId) {
      editInputRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
    }
  }, [editedTitle, editingConversationId]);

  const handleConversationClick = (conversationId: string) => {
    navigate(`/chat/${conversationId}`);
  };

  const handleEdit = (e: React.MouseEvent, conversation: Conversation) => {
    e.stopPropagation();
    setEditingConversationId(conversation.id);
    setEditedTitle(conversation.title || 'Untitled Conversation');
  };

  const handleSaveEdit = async (e: React.MouseEvent, conversation: Conversation) => {
    e.stopPropagation();
    if (!editedTitle.trim()) {
      return;
    }
    try {
      await updateConversation(conversation.id, editedTitle.trim());
      // Notify parent component of the update
      if (onUpdate) {
        onUpdate(conversation.id, editedTitle.trim());
      }
      setEditingConversationId(null);
      setEditedTitle('');
    } catch (error) {
      console.error('Error updating conversation:', error);
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingConversationId(null);
    setEditedTitle('');
  };

  const handleDelete = (e: React.MouseEvent, conversation: Conversation) => {
    e.stopPropagation();
    setConversationToDelete(conversation);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (conversationToDelete) {
      try {
        await deleteConversation(conversationToDelete.id);
        setShowDeleteModal(false);
        setConversationToDelete(null);
        // Notify parent component if callback is provided
        if (onDelete) {
          onDelete(conversationToDelete.id);
        }
      } catch (error) {
        console.error('Error deleting conversation:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setConversationToDelete(null);
  };

  const getProjectForConversation = (projectId?: string) => {
    if (!projectId) return null;
    return projects.find(p => p.id === projectId);
  };

  const columns: ColumnDef<Conversation>[] = [
    {
      accessorKey: 'createdAt',
      header: 'Date',
      cell: ({ getValue }) => {
        const date = new Date(getValue() as Date);
        return (
          <span className="conversation-table-cell-text">
            {date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </span>
        );
      },
    },
    {
      accessorKey: 'title',
      header: 'Initial Prompt',
      cell: ({ row }) => {
        const isEditing = editingConversationId === row.original.id;

        if (isEditing) {
          return (
            <div className="d-flex align-items-start flex-column" onClick={(e) => e.stopPropagation()}>
              <input
                ref={editInputRef}
                type="text"
                className="form-control form-control-sm conversation-edit-input"
                value={editedTitle}
                onChange={(e) => {
                  cursorPositionRef.current = e.target.selectionStart || 0;
                  setEditedTitle(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveEdit(e as any, row.original);
                  } else if (e.key === 'Escape') {
                    handleCancelEdit(e as any);
                  }
                }}
                onClick={(e) => {
                  cursorPositionRef.current = (e.target as HTMLInputElement).selectionStart || 0;
                }}
                onKeyUp={(e) => {
                  cursorPositionRef.current = (e.target as HTMLInputElement).selectionStart || 0;
                }}
                autoFocus
              />
            </div>
          );
        }

        return (
          <div
            className="d-flex align-items-start flex-column conversation-title-cell"
            onClick={() => handleConversationClick(row.original.id)}
          >
            <div className="conversation-title-text">
              {row.original.title || 'Untitled Conversation'}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'projectId',
      header: 'Project',
      cell: ({ row }) => {
        const project = getProjectForConversation(row.original.projectId);
        return project ? (
          <span className="conversation-table-cell-text text-muted">{project.name}</span>
        ) : (
          <span className="conversation-table-cell-text text-muted">No project</span>
        );
      },
    },
    {
      accessorKey: 'artifactCount',
      header: 'Artifact Count',
      cell: ({ getValue }) => (
        <span className="conversation-table-cell-text">
          {getValue() as number || 0}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Action',
      cell: ({ row }) => {
        const isEditing = editingConversationId === row.original.id;

        if (isEditing) {
          return (
            <div className="d-flex gap-2 edit-container" onClick={(e) => e.stopPropagation()}>
              <button
                className="btn btn-link p-0 edit-save table-action-btn"
                aria-label="Save changes"
                onClick={(e) => handleSaveEdit(e, row.original)}
              >
                <Check size={24} />
              </button>
              <button
                className="btn btn-link p-0 edit-cancel table-action-btn"
                aria-label="Cancel editing"
                onClick={handleCancelEdit}
              >
                <X size={24} />
              </button>
            </div>
          );
        }

        return (
          <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
            <button
              className="btn btn-link text-muted p-0 table-action-btn"
              aria-label="Edit conversation"
              onClick={(e) => handleEdit(e, row.original)}
            >
              <Pencil size={16} />
            </button>
            <button
              className="btn btn-link text-danger p-0 table-action-btn"
              aria-label="Delete conversation"
              onClick={(e) => handleDelete(e, row.original)}
            >
              <Trash size={16} />
            </button>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: conversations,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  if (conversations.length === 0) {
    return (
      <div className="text-center py-4 text-muted">
        No conversations yet
      </div>
    );
  }

  return (
    <>
      <div className="conversation-table-container">
        <table className="conversation-table">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={header.column.getCanSort() ? '' : 'no-sort'}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className="d-flex align-items-center"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: <CaretUpFill className="ms-2" size={12} />,
                            desc: <CaretDownFill className="ms-2" size={12} />,
                          }[header.column.getIsSorted() as string] ?? (
                            header.column.getCanSort() ? (
                              <span className="ms-2 sort-indicator-inactive-opacity">
                                <CaretUpFill size={12} />
                              </span>
                            ) : null
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        itemType="Conversation"
        artifactCount={conversationToDelete?.artifactCount || 0}
      />
    </>
  );
};

export default ConversationTable;